import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Loader2,
  Building2,
  Users,
  TrendingUp,
  Trash2,
  Pencil,
  X,
  Settings,
  Bot,
  Check,
  DollarSign,
  Gift,
  Download,
  Mail,
  Ticket,
  ArrowUp,
  ArrowDown,

} from 'lucide-react';
import { VouchersSection } from '@/components/admin/VouchersSection';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';

import { EmpresasSummaryCard } from '@/components/admin/EmpresasSummaryCard';
import { UsuariosSummaryCard, UsuarioStats } from '@/components/admin/UsuariosSummaryCard';
import { FeedbacksSummaryCard } from '@/components/admin/FeedbacksSummaryCard';
import { LeadsSummaryCard } from '@/components/admin/LeadsSummaryCard';
import {
  ConsumoRow,
  Empresa,
  FeedbackStats,
  InatividadeRow,
  LeadCalculadora,
  PAPEIS,
  PLANOS,
  PLANO_LABELS,
  Usuario,
} from '@/components/admin/types';

interface EmpresaVoucher {
  empresa_id: string;
  codigo: string;
  data_vencimento: string;
}

interface TokenUsage {
  empresa_id: string;
  user_id: string;
  provider: string;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
}

interface CostSummary {
  totalUsd: number;
  totalBrl: number;
  inputTokens: number;
  outputTokens: number;
  byProvider: {
    lovable: { cost: number; requests: number };
    openai: { cost: number; requests: number };
  };
}

const USD_TO_BRL = 5.50; // Taxa de conversão fixa


const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [totalUsuarios, setTotalUsuarios] = useState(0);
  const [leads, setLeads] = useState<LeadCalculadora[]>([]);
  const [empresaVouchers, setEmpresaVouchers] = useState<EmpresaVoucher[]>([]);
  const [sendingReminderId, setSendingReminderId] = useState<string | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [empresasPage, setEmpresasPage] = useState(1);
  const [semEmpresaPage, setSemEmpresaPage] = useState(1);
  const [sendingConviteId, setSendingConviteId] = useState<string | null>(null);

  const handleSendConviteExpert = async (empresaId: string) => {
    setSendingConviteId(empresaId);
    try {
      const { data, error } = await supabase.functions.invoke('convite-expert', {
        body: { empresa_id: empresaId },
      });
      if (error || !data?.ok) {
        throw new Error(data?.detail || data?.error || error?.message || 'Falha ao enviar');
      }
      setEmpresas((prev) =>
        prev.map((e) =>
          e.id === empresaId ? { ...e, convite_expert_enviado_em: new Date().toISOString() } : e
        )
      );
      toast({
        title: 'Convite enviado',
        description: `Voucher ${data.codigo} enviado para ${data.destinatario}.`,
      });
    } catch (e) {
      toast({
        title: 'Erro',
        description: (e as Error)?.message || 'Não foi possível enviar o convite.',
        variant: 'destructive',
      });
    } finally {
      setSendingConviteId(null);
    }
  };

  const handleSendCadastroReminder = async (usuarioId: string) => {
    setSendingReminderId(usuarioId);
    try {
      const { data, error } = await supabase.functions.invoke('send-cadastro-incompleto', {
        body: { usuario_id: usuarioId },
      });
      if (error || !data?.ok) {
        throw new Error(data?.detail || error?.message || 'Falha ao enviar');
      }
      toast({ title: 'Email enviado', description: 'Lembrete enviado com sucesso.' });
    } catch (e) {
      toast({
        title: 'Erro',
        description: (e as Error)?.message || 'Não foi possível enviar o email.',
        variant: 'destructive',
      });
    } finally {
      setSendingReminderId(null);
    }
  };

  const [deleteUserTarget, setDeleteUserTarget] = useState<{ id: string; nome: string | null; email: string } | null>(null);

  const handleDeleteUsuario = async (usuarioId: string) => {
    setDeletingUserId(usuarioId);
    try {
      const { data, error } = await supabase.functions.invoke('delete-usuario', {
        body: { usuario_id: usuarioId },
      });
      if (error || !data?.ok) {
        throw new Error(data?.detail || data?.error || error?.message || 'Falha ao deletar');
      }
      setUsuarios((prev) => prev.filter((u) => u.id !== usuarioId));
      toast({ title: 'Usuário deletado', description: 'Cadastro removido com sucesso.' });
    } catch (e) {
      toast({
        title: 'Erro',
        description: (e as Error)?.message || 'Não foi possível deletar o usuário.',
        variant: 'destructive',
      });
    } finally {
      setDeletingUserId(null);
      setDeleteUserTarget(null);
    }
  };

  
  // Modal states
  const [showEmpresasModal, setShowEmpresasModal] = useState(false);
  const [showUsuariosModal, setShowUsuariosModal] = useState(false);
  const [showCostModal, setShowCostModal] = useState(false);
  const [showLeadsModal, setShowLeadsModal] = useState(false);
  const [costModalType, setCostModalType] = useState<'empresa' | 'usuario'>('empresa');
  const [costModalId, setCostModalId] = useState<string | null>(null);
  const [costModalName, setCostModalName] = useState<string>('');
  const [costSummary, setCostSummary] = useState<CostSummary | null>(null);
  const [loadingCost, setLoadingCost] = useState(false);
  
  // Edit states
  // AI Provider state
  const [aiProvider, setAiProvider] = useState<'lovable' | 'openai'>('lovable');
  const [savingAiProvider, setSavingAiProvider] = useState(false);
  const [feedbackStats, setFeedbackStats] = useState<FeedbackStats | null>(null);
  const [consumos, setConsumos] = useState<ConsumoRow[]>([]);
  const [inatividade, setInatividade] = useState<Record<string, InatividadeRow>>({});
  const [personas, setPersonas] = useState<{ id: string; user_id: string }[]>([]);




  // ========= Helpers =========

  const fetchDashboard = async () => {
    const { data: empresasData, error: empErr } = await supabase
      .from('empresas')
      .select('*')
      .order('created_at', { ascending: false });

    if (empErr) {
      console.error('Erro ao carregar empresas:', empErr);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar as empresas.',
        variant: 'destructive',
      });
    } else if (empresasData) {
      setEmpresas(empresasData as Empresa[]);
    }

    const { data: usuariosData, count, error: usrErr } = await supabase
      .from('usuarios')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false });

    if (usrErr) {
      console.error('Erro ao carregar usuários:', usrErr);
    } else if (usuariosData) {
      setUsuarios(usuariosData as Usuario[]);
    }
    setTotalUsuarios(count || 0);

    // Fetch AI provider configuration
    const { data: configData } = await supabase
      .from('configuracoes')
      .select('valor')
      .eq('chave', 'ai_provider')
      .single();
    
    if (configData?.valor) {
      setAiProvider(configData.valor as 'lovable' | 'openai');
    }

    // Estatísticas de consumo de feedbacks
    const { data: statsData, error: statsErr } = await (supabase as any).rpc('admin_feedback_stats');
    if (statsErr) {
      console.error('Erro ao carregar estatísticas de feedbacks:', statsErr);
    } else if (statsData?.[0]) {
      setFeedbackStats({
        mesAtual: Number(statsData[0].mes_atual) || 0,
        mesAnterior: Number(statsData[0].mes_anterior) || 0,
        total: Number(statsData[0].total) || 0,
        custoMesAtual: Number(statsData[0].custo_mes_atual) || 0,
        custoTotal: Number(statsData[0].custo_total) || 0,
      });

    }

    // Histórico de consumo de feedbacks (custo por feedback)
    const { data: consumoData, error: consumoErr } = await (supabase as any)
      .from('feedback_consumo')
      .select('id, empresa_id, user_id, persona_id, plano, cost_usd, input_tokens, output_tokens, model, created_at')
      .order('created_at', { ascending: false })
      .limit(500);
    if (consumoErr) {
      console.error('Erro ao carregar consumo de feedbacks:', consumoErr);
    } else if (consumoData) {
      setConsumos(consumoData as ConsumoRow[]);
    }

    // Inatividade por empresa (dias sem uso e nível do lembrete)
    const { data: inatData, error: inatErr } = await (supabase as any).rpc(
      'admin_empresas_inatividade'
    );
    if (inatErr) {
      console.error('Erro ao carregar inatividade:', inatErr);
    } else if (inatData) {
      const map: Record<string, InatividadeRow> = {};
      for (const r of inatData as any[]) {
        map[r.empresa_id] = {
          dias: Number(r.dias_sem_uso) || 0,
          nivel: Number(r.lembrete_nivel) || 0,
          lembreteEm: r.lembrete_em ?? null,
        };
      }
      setInatividade(map);
    }



    // Personas por usuário
    const { data: personasData, error: personasErr } = await supabase
      .from('personas')
      .select('id, user_id')
      .order('created_at', { ascending: false });
    if (personasErr) {
      console.error('Erro ao carregar personas:', personasErr);
    } else if (personasData) {
      setPersonas(personasData as { id: string; user_id: string }[]);
    }
  };



  const fetchLeads = async () => {
    const { data, error } = await supabase
      .from('leads_calculadora' as any)
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) {
      setLeads(data as any as LeadCalculadora[]);
    }
  };

  const fetchEmpresaVouchers = async () => {
    const { data, error } = await supabase
      .from('vouchers' as any)
      .select('codigo, data_vencimento, empresa_id')
      .not('empresa_id', 'is', null);
    if (!error && data) {
      setEmpresaVouchers(data as any as EmpresaVoucher[]);
    }
  };

  const voucherByEmpresa = (empresaId: string) =>
    empresaVouchers.find((v) => v.empresa_id === empresaId);

  const exportLeadsCSV = () => {
    if (leads.length === 0) return;
    const headers = ['Email', 'Nome', 'Empresa', 'Vendedores', 'Ticket Médio', 'Vendas/Mês', 'Taxa Conversão', 'ROI Calculado', 'Receita Extra', 'Data'];
    const rows = leads.map(l => [
      l.email,
      l.nome || '',
      l.empresa || '',
      l.vendedores ?? '',
      l.ticket_medio ?? '',
      l.vendas_mes ?? '',
      l.taxa_conversao ?? '',
      l.roi_calculado ?? '',
      l.receita_extra_calculada ?? '',
      new Date(l.created_at).toLocaleDateString('pt-BR'),
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-calculadora-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchCostByEmpresa = async (empresaId: string) => {
    setLoadingCost(true);
    try {
      const { data, error } = await supabase
        .from('token_usage')
        .select('*')
        .eq('empresa_id', empresaId);

      if (error) throw error;

      const summary = calculateCostSummary(data || []);
      setCostSummary(summary);
    } catch (error) {
      console.error('Erro ao buscar custos:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os custos.',
        variant: 'destructive',
      });
    } finally {
      setLoadingCost(false);
    }
  };

  const fetchCostByUsuario = async (usuarioId: string) => {
    setLoadingCost(true);
    try {
      const { data, error } = await supabase
        .from('token_usage')
        .select('*')
        .eq('user_id', usuarioId);

      if (error) throw error;

      const summary = calculateCostSummary(data || []);
      setCostSummary(summary);
    } catch (error) {
      console.error('Erro ao buscar custos:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os custos.',
        variant: 'destructive',
      });
    } finally {
      setLoadingCost(false);
    }
  };

  const calculateCostSummary = (data: TokenUsage[]): CostSummary => {
    const summary: CostSummary = {
      totalUsd: 0,
      totalBrl: 0,
      inputTokens: 0,
      outputTokens: 0,
      byProvider: {
        lovable: { cost: 0, requests: 0 },
        openai: { cost: 0, requests: 0 },
      },
    };

    data.forEach((usage) => {
      summary.totalUsd += Number(usage.cost_usd) || 0;
      summary.inputTokens += usage.input_tokens || 0;
      summary.outputTokens += usage.output_tokens || 0;

      if (usage.provider === 'lovable') {
        summary.byProvider.lovable.cost += Number(usage.cost_usd) || 0;
        summary.byProvider.lovable.requests += 1;
      } else if (usage.provider === 'openai') {
        summary.byProvider.openai.cost += Number(usage.cost_usd) || 0;
        summary.byProvider.openai.requests += 1;
      }
    });

    summary.totalBrl = summary.totalUsd * USD_TO_BRL;

    return summary;
  };

  const openCostModal = (type: 'empresa' | 'usuario', id: string, name: string) => {
    setCostModalType(type);
    setCostModalId(id);
    setCostModalName(name);
    setCostSummary(null);
    setShowCostModal(true);

    if (type === 'empresa') {
      fetchCostByEmpresa(id);
    } else {
      fetchCostByUsuario(id);
    }
  };

  useEffect(() => {
    const checkAccess = async () => {
      if (authLoading) return;

      if (!user) {
        navigate('/auth');
        return;
      }

      try {
        const { data: usuario, error: userErr } = await supabase
          .from('usuarios')
          .select('papel')
          .eq('id', user.id)
          .single();

        if (userErr) {
          console.error('Erro ao verificar papel:', userErr);
          navigate('/dashboard');
          return;
        }

        if (!usuario || usuario.papel !== 'admin') {
          navigate('/dashboard');
          return;
        }

        await fetchDashboard();
        await fetchLeads();
        await fetchEmpresaVouchers();
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        toast({
          title: 'Erro',
          description: 'Não foi possível carregar os dados.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [user, authLoading, navigate]);

  // ========= Atualização =========

  const updateEmpresa = async (id: string, patch: Partial<Empresa>) => {
    const prev = empresas;
    const next = empresas.map((e) => (e.id === id ? { ...e, ...patch } : e));
    setEmpresas(next);

    const { error } = await supabase.from('empresas').update(patch).eq('id', id);

    if (error) {
      setEmpresas(prev);
      if ((error as any).code === '42501') {
        toast({
          title: 'Sem permissão',
          description: 'Você não pode alterar este campo.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Erro ao salvar',
          description: error.message,
          variant: 'destructive',
        });
      }
      return false;
    } else {
      toast({
        title: 'Salvo',
        description: 'Alteração aplicada com sucesso.',
      });
      return true;
    }
  };

  const handlePlanoChange = (id: string, value: Empresa['plano']) => {
    void updateEmpresa(id, { plano: value });
  };

  const handleFeedbacksChange = (id: string, value: number) => {
    const safe = Math.max(0, Math.floor(Number.isFinite(value) ? value : 0));
    void updateEmpresa(id, { feedbacks: safe });
  };

  // ========= Deleção =========

  const handleDeleteEmpresa = async (empresaId: string) => {
    try {
      const { error } = await supabase.from('empresas').delete().eq('id', empresaId);
      if (error) throw error;

      toast({
        title: 'Empresa excluída',
        description: 'A empresa foi excluída com sucesso.',
      });

      await fetchDashboard();
    } catch (error: any) {
      console.error('Erro ao excluir empresa:', error);
      toast({
        title: 'Erro',
        description: error?.message || 'Não foi possível excluir a empresa.',
        variant: 'destructive',
      });
    }
  };

  // ========= Edição de Usuário =========

  const saveUserEdit = async (
    usuarioId: string,
    patch: { nome: string; papel: Usuario['papel']; empresa_id: string | null }
  ) => {
    try {
      const { error } = await supabase.from('usuarios').update(patch).eq('id', usuarioId);

      if (error) throw error;

      toast({
        title: 'Usuário atualizado',
        description: 'As alterações foram salvas com sucesso.',
      });

      await fetchDashboard();
    } catch (error: any) {
      console.error('Erro ao atualizar usuário:', error);
      toast({
        title: 'Erro',
        description: error?.message || 'Não foi possível atualizar o usuário.',
        variant: 'destructive',
      });
    }
  };


  const handleAiProviderChange = async (newProvider: 'lovable' | 'openai') => {
    setSavingAiProvider(true);
    try {
      const { error } = await supabase
        .from('configuracoes')
        .update({ valor: newProvider })
        .eq('chave', 'ai_provider');

      if (error) throw error;

      setAiProvider(newProvider);
      toast({
        title: 'Configuração salva',
        description: `Provedor de IA alterado para ${newProvider === 'lovable' ? 'Lovable AI' : 'OpenAI'}.`,
      });
    } catch (error: any) {
      console.error('Erro ao salvar configuração:', error);
      toast({
        title: 'Erro',
        description: error?.message || 'Não foi possível salvar a configuração.',
        variant: 'destructive',
      });
    } finally {
      setSavingAiProvider(false);
    }
  };

  const getEmpresaNome = (empresaId: string | null) => {
    if (!empresaId) return null;
    const empresa = empresas.find((e) => e.id === empresaId);
    return empresa?.nome || null;
  };

  const formatCurrency = (value: number, currency: 'USD' | 'BRL') => {
    if (currency === 'USD') {
      return `$${value.toFixed(4)}`;
    }
    return `R$ ${value.toFixed(2)}`;
  };

  const usuariosSemEmpresa = usuarios.filter(
    (u) => !u.empresa_id && u.papel === 'coordenador'
  );
  const semEmpresaTotalPages = Math.max(1, Math.ceil(usuariosSemEmpresa.length / 10));
  const empresasTotalPages = Math.max(1, Math.ceil(empresas.length / 20));


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/10 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  const totalFeedbacks = empresas.reduce((sum, emp) => sum + (emp.feedbacks || 0), 0);

  // Custos médios
  const custoTotalGeral = feedbackStats?.custoTotal ?? 0;
  const custoMedioPorEmpresa = empresas.length > 0 ? custoTotalGeral / empresas.length : 0;
  const custoMedioPorFeedback =
    (feedbackStats?.total ?? 0) > 0 ? custoTotalGeral / (feedbackStats!.total) : 0;

  const planoLabels: Record<string, string> = { start: 'Start', expert: 'Expert', max_pro: 'Max Pro' };
  const custoMedioPorPlano = (['start', 'expert', 'max_pro'] as const).map((plano) => {
    const empresasPlano = empresas.filter((e) => e.plano === plano);
    const custo = consumos
      .filter((c) => (c.plano ?? empresas.find((e) => e.id === c.empresa_id)?.plano) === plano)
      .reduce((s, c) => s + (Number(c.cost_usd) || 0), 0);
    return {
      plano,
      label: planoLabels[plano],
      empresas: empresasPlano.length,
      media: empresasPlano.length > 0 ? custo / empresasPlano.length : 0,
    };
  });

  const empresaNome = (id: string | null) => empresas.find((e) => e.id === id)?.nome || '—';
  const usuarioNome = (id: string | null) => {
    const u = usuarios.find((x) => x.id === id);
    return u ? u.nome || u.email : '—';
  };

  // Estatísticas por usuário (personas, feedbacks e custo)
  const statsByUser: Record<string, UsuarioStats> = {};
  for (const p of personas) {
    const s = (statsByUser[p.user_id] ??= { personas: 0, feedbacks: 0, custo: 0 });
    s.personas += 1;
  }
  for (const c of consumos) {
    if (!c.user_id) continue;
    const s = (statsByUser[c.user_id] ??= { personas: 0, feedbacks: 0, custo: 0 });
    s.feedbacks += 1;
    s.custo += Number(c.cost_usd) || 0;
  }



  // ========= UI =========

  return (
    <Layout title="Administração">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Painel Administrativo</h1>
          <p className="text-muted-foreground mt-2">
            Gerencie todas as empresas e usuários do sistema
          </p>
        </div>

        {/* Cards de resumo */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <EmpresasSummaryCard
            empresas={empresas}
            custoMedioPorEmpresa={custoMedioPorEmpresa}
            custoMedioPorPlano={custoMedioPorPlano}
            inatividade={inatividade}
            onOpenEmpresa={(id) => navigate(`/admin/empresa/${id}`)}
            onOpenCost={(id, nome) => openCostModal('empresa', id, nome)}
          />

          <UsuariosSummaryCard
            usuarios={usuarios}
            totalUsuarios={totalUsuarios}
            empresas={empresas}
            statsByUser={statsByUser}
            totalPersonas={personas.length}
            onOpenCost={(id, nome) => openCostModal('usuario', id, nome)}
            onSaveUser={saveUserEdit}
          />

          <FeedbacksSummaryCard
            stats={feedbackStats}
            consumos={consumos}
            totalDisponiveis={totalFeedbacks}
            custoMedioPorFeedback={custoMedioPorFeedback}
            empresaNome={empresaNome}
            usuarioNome={usuarioNome}
          />

          <LeadsSummaryCard leads={leads} onRefresh={fetchLeads} />
        </div>


        {/* Card de Configuração de IA */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configurações de IA
            </CardTitle>
            <CardDescription>
              Configure qual provedor de IA será usado para os treinamentos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <p className="text-sm font-medium mb-1">Provedor de IA</p>
                <p className="text-xs text-muted-foreground">
                  {aiProvider === 'lovable' 
                    ? 'Usando Lovable AI (gratuito, sem necessidade de API key)'
                    : 'Usando OpenAI GPT-4 (requer chave de API configurada)'}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={aiProvider === 'lovable' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleAiProviderChange('lovable')}
                  disabled={savingAiProvider || aiProvider === 'lovable'}
                  className="flex items-center gap-2"
                >
                  <Bot className="w-4 h-4" />
                  Lovable AI
                </Button>
                <Button
                  variant={aiProvider === 'openai' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleAiProviderChange('openai')}
                  disabled={savingAiProvider || aiProvider === 'openai'}
                  className="flex items-center gap-2"
                >
                  OpenAI
                </Button>
              </div>
            </div>
            {aiProvider === 'openai' && (
              <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  ⚠️ Para usar OpenAI, certifique-se de que a chave OPENAI_API_KEY está configurada nas variáveis de ambiente.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Setup Stripe */}
        <Card>
          <CardHeader>
            <CardTitle>Configuração da Stripe</CardTitle>
            <CardDescription>
              Cria/atualiza os produtos e preços (Expert e Max Pro, mensal e anual) na conta Stripe conectada.
              Idempotente — pode rodar novamente com segurança.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={async () => {
                const { data, error } = await supabase.functions.invoke('stripe-setup');
                if (error) {
                  toast({ title: 'Erro no setup', description: error.message, variant: 'destructive' });
                } else {
                  toast({ title: 'Setup concluído', description: 'Produtos e preços sincronizados na Stripe.' });
                  console.log('stripe-setup result', data);
                }
              }}
            >
              Rodar setup dos produtos na Stripe
            </Button>
          </CardContent>
        </Card>

        {/* Gestão de Vouchers */}
        <VouchersSection />


        {/* Usuários sem empresa */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Usuários sem empresa ({usuariosSemEmpresa.length})
            </CardTitle>
            <CardDescription>
              Usuários cadastrados que ainda não vincularam uma empresa. Envie um lembrete para finalizar o cadastro.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Papel</TableHead>
                  <TableHead>Cadastro</TableHead>
                  <TableHead className="w-[180px]">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usuariosSemEmpresa
                  .slice((semEmpresaPage - 1) * 10, semEmpresaPage * 10)
                  .map((u) => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">{u.nome || '-'}</TableCell>
                      <TableCell className="text-muted-foreground">{u.email}</TableCell>
                      <TableCell>{PAPEIS.find((p) => p.value === u.papel)?.label || u.papel}</TableCell>
                      <TableCell>
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('pt-BR') : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={sendingReminderId === u.id}
                            onClick={() => handleSendCadastroReminder(u.id)}
                          >
                            {sendingReminderId === u.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Mail className="w-4 h-4 mr-2" />
                            )}
                            Lembrete
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={deletingUserId === u.id}
                            onClick={() => setDeleteUserTarget({ id: u.id, nome: u.nome, email: u.email })}
                          >
                            {deletingUserId === u.id ? (
                              <Loader2 className="w-4 h-4 text-destructive animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4 text-destructive" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                {usuariosSemEmpresa.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                      Todos os usuários já estão vinculados a uma empresa.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {semEmpresaTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Página {semEmpresaPage} de {semEmpresaTotalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={semEmpresaPage <= 1}
                    onClick={() => setSemEmpresaPage((p) => Math.max(1, p - 1))}
                  >
                    Anterior
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={semEmpresaPage >= semEmpresaTotalPages}
                    onClick={() => setSemEmpresaPage((p) => Math.min(semEmpresaTotalPages, p + 1))}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>



        {/* Tabela de empresas */}
        <Card>
          <CardHeader>
            <CardTitle>Empresas Cadastradas</CardTitle>
            <CardDescription>Edite plano e feedbacks diretamente na listagem</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Plano</TableHead>
                  <TableHead>Voucher</TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead>Feedbacks</TableHead>
                  <TableHead>Cadastro</TableHead>
                  <TableHead className="w-[120px]">Ações</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {empresas.slice((empresasPage - 1) * 20, empresasPage * 20).map((empresa) => (
                  <TableRow key={empresa.id} className="hover:bg-muted/50 transition-colors">
                    {/* Nome */}
                    <TableCell
                      className="font-medium cursor-pointer"
                      onClick={() => navigate(`/admin/empresa/${empresa.id}`)}
                    >
                      <div>{empresa.nome}</div>
                      {(() => {
                        const coord = usuarios.find(
                          (u) => u.empresa_id === empresa.id && u.papel === 'coordenador'
                        );
                        if (!coord) return null;
                        return (
                          <div className="text-xs text-muted-foreground font-normal mt-0.5">
                            {coord.nome} · {coord.email}
                          </div>
                        );
                      })()}
                    </TableCell>

                    {/* Plano - SELECT ENUM */}
                    <TableCell>
                      <div onClick={(e) => e.stopPropagation()} className="flex items-center">
                        <select
                          className="border rounded px-2 py-1 text-sm bg-background"
                          defaultValue={empresa.plano}
                          onChange={(e) =>
                            handlePlanoChange(empresa.id, e.target.value as Empresa['plano'])
                          }
                        >
                          {PLANOS.map((p) => (
                            <option key={p.value} value={p.value}>
                              {p.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </TableCell>

                    {/* Voucher */}
                    <TableCell>
                      {(() => {
                        const v = voucherByEmpresa(empresa.id);
                        if (!v) return <span className="text-muted-foreground text-xs">—</span>;
                        const expirado = new Date(v.data_vencimento) < new Date();
                        return (
                          <div className="text-xs">
                            <p className="font-mono font-medium">{v.codigo}</p>
                            <p className={expirado ? 'text-destructive' : 'text-muted-foreground'}>
                              {expirado ? 'Expirou ' : 'Expira '}
                              {new Date(v.data_vencimento).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                        );
                      })()}
                    </TableCell>

                    {/* Produto */}
                    <TableCell
                      className="cursor-pointer"
                      onClick={() => navigate(`/admin/empresa/${empresa.id}`)}
                    >
                      {empresa.produto || '-'}
                    </TableCell>

                    {/* Feedbacks */}
                    <TableCell>
                      <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-2">
                        <input
                          type="number"
                          min={0}
                          className="w-24 border rounded px-2 py-1 text-sm bg-background"
                          defaultValue={empresa.feedbacks}
                          onBlur={(e) => {
                            const n = Number(e.currentTarget.value);
                            if (!Number.isNaN(n) && n !== empresa.feedbacks)
                              handleFeedbacksChange(empresa.id, n);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const n = Number((e.target as HTMLInputElement).value);
                              if (!Number.isNaN(n) && n !== empresa.feedbacks)
                                handleFeedbacksChange(empresa.id, n);
                              (e.target as HTMLInputElement).blur();
                            }
                          }}
                        />
                        <div className="flex gap-1">
                          <Button
                            size="icon"
                            variant="secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFeedbacksChange(
                                empresa.id,
                                (empresa.feedbacks || 0) - 1
                              );
                            }}
                            disabled={(empresa.feedbacks || 0) <= 0}
                            title="Diminuir"
                          >
                            −
                          </Button>
                          <Button
                            size="icon"
                            variant="secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFeedbacksChange(
                                empresa.id,
                                (empresa.feedbacks || 0) + 1
                              );
                            }}
                            title="Aumentar"
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    </TableCell>

                    {/* Data de criação */}
                    <TableCell>
                      {empresa.created_at
                        ? new Date(empresa.created_at).toLocaleDateString('pt-BR')
                        : '-'}
                    </TableCell>

                    {/* Ações - custos e excluir */}
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            openCostModal('empresa', empresa.id, empresa.nome);
                          }}
                          title="Ver custos"
                        >
                          <DollarSign className="h-4 w-4 text-green-600" />
                        </Button>
                        {(() => {
                          const motivoBloqueio = empresa.convite_expert_enviado_em
                            ? `Convite já enviado em ${new Date(empresa.convite_expert_enviado_em).toLocaleDateString('pt-BR')}`
                            : empresa.voucher_id
                              ? 'Esta empresa já utilizou um voucher anteriormente'
                              : empresa.plano !== 'start'
                                ? `Empresa já possui plano ${empresa.plano === 'expert' ? 'Expert' : 'Max Pro'} ativo`
                                : null;
                          const bloqueado = !!motivoBloqueio;
                          const botao = (
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={bloqueado || sendingConviteId === empresa.id}
                            >
                              {sendingConviteId === empresa.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Gift
                                  className={`h-4 w-4 ${bloqueado ? 'text-muted-foreground' : 'text-primary'}`}
                                />
                              )}
                            </Button>
                          );

                          if (bloqueado) {
                            return (
                              <span
                                title={motivoBloqueio}
                                className="inline-flex cursor-not-allowed"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {botao}
                              </span>
                            );
                          }

                          return (
                            <AlertDialog>
                              <AlertDialogTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <span title="Enviar convite Expert (1 mês grátis)" className="inline-flex">
                                  {botao}
                                </span>
                              </AlertDialogTrigger>

                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Enviar convite Expert?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Será gerado um voucher exclusivo do plano Expert (válido por 1 mês,
                                até 4 colaboradores) e enviado por email ao coordenador da empresa "
                                {empresa.nome}". Este envio só pode ser feito uma única vez.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleSendConviteExpert(empresa.id)}
                              >
                                Enviar convite
                              </AlertDialogAction>
                            </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          );
                        })()}

                        <AlertDialog>
                          <AlertDialogTrigger
                            asChild
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button variant="ghost" size="icon">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Excluir Empresa</AlertDialogTitle>
                              <AlertDialogDescription>
                                Tem certeza que deseja excluir a empresa "
                                {empresa.nome}"? Esta ação não pode ser desfeita.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteEmpresa(empresa.id)}
                              >
                                Excluir
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}

                {empresas.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center py-10 text-muted-foreground"
                    >
                      Nenhuma empresa cadastrada.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {empresasTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Página {empresasPage} de {empresasTotalPages} · {empresas.length} empresas
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={empresasPage <= 1}
                    onClick={() => setEmpresasPage((p) => Math.max(1, p - 1))}
                  >
                    Anterior
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={empresasPage >= empresasTotalPages}
                    onClick={() => setEmpresasPage((p) => Math.min(empresasTotalPages, p + 1))}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>



        {/* Modal de Custos */}
        <Dialog open={showCostModal} onOpenChange={setShowCostModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Custos - {costModalName}
              </DialogTitle>
            </DialogHeader>
            {loadingCost ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : costSummary ? (
              <div className="space-y-4">
                {/* Resumo Principal */}
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-muted-foreground">Total (USD)</p>
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(costSummary.totalUsd, 'USD')}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-muted-foreground">Total (BRL)</p>
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(costSummary.totalBrl, 'BRL')}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Tokens */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Uso de Tokens</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Input:</span>
                      <span className="font-medium">{costSummary.inputTokens.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Output:</span>
                      <span className="font-medium">{costSummary.outputTokens.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t mt-2">
                      <span className="text-muted-foreground">Total:</span>
                      <span className="font-bold">
                        {(costSummary.inputTokens + costSummary.outputTokens).toLocaleString()}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Por Provedor */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Por Provedor</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Bot className="w-4 h-4" />
                        <span className="text-sm">Lovable AI</span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">
                          {formatCurrency(costSummary.byProvider.lovable.cost, 'USD')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {costSummary.byProvider.lovable.requests} requisições
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className="w-4 h-4 flex items-center justify-center text-xs font-bold">
                          AI
                        </span>
                        <span className="text-sm">OpenAI</span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">
                          {formatCurrency(costSummary.byProvider.openai.cost, 'USD')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {costSummary.byProvider.openai.requests} requisições
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <p className="text-xs text-muted-foreground text-center">
                  Taxa de conversão: 1 USD = R$ {USD_TO_BRL.toFixed(2)}
                </p>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum dado de consumo encontrado.
              </div>
            )}
          </DialogContent>
        </Dialog>


        <AlertDialog open={!!deleteUserTarget} onOpenChange={(open) => !open && setDeleteUserTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Deletar usuário?</AlertDialogTitle>
              <AlertDialogDescription>
                Você está prestes a deletar definitivamente{' '}
                <strong>{deleteUserTarget?.nome || deleteUserTarget?.email}</strong>
                {deleteUserTarget?.nome ? ` (${deleteUserTarget?.email})` : ''}. Essa ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={!!deletingUserId}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                disabled={!!deletingUserId}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={(e) => {
                  e.preventDefault();
                  if (deleteUserTarget) handleDeleteUsuario(deleteUserTarget.id);
                }}
              >
                {deletingUserId ? 'Deletando...' : 'Deletar'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default Admin;
